/**
 * Roles and permissions.
 *
 * The owner can build a role out of any combination of the things the system can
 * do, in plain words - "can authorise a late exchange", not "exchange.outside_window".
 * The three roles the shop starts with are only a starting point.
 *
 * Two safety rails, both deliberate:
 *   - the Owner role always holds everything, so the shop cannot lock itself out;
 *   - a role still being used by somebody cannot be deleted.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  PERMISSIONS,
  PERMISSION_GROUPS,
  permissionInFeatures,
  type Permission,
  type PermissionGroup,
  type PermissionMeta,
  type RoleRecord,
} from '@pos/shared';
import { useI18n, useT } from '../../core/i18n/index.js';
import { useAuth } from '../../core/state/auth.js';
import { useToast } from '../../core/state/toast.js';
import { api } from '../../core/api.js';
import { Button, Confirm, Empty, Field, Loading, Modal, Pill } from '../../core/ui/ui.js';

/**
 * Only the permissions of modules this shop has. A switch for a module the shop
 * does not have would do nothing when ticked, which reads as a broken screen.
 */
function useShopPermissions(): PermissionMeta[] {
  const { features } = useAuth();
  return useMemo(() => PERMISSIONS.filter((p) => permissionInFeatures(p.key, features)), [features]);
}

export function Roles() {
  const t = useT();
  const { lang } = useI18n();
  const { can, refreshUser } = useAuth();
  const toast = useToast();

  const [roles, setRoles] = useState<RoleRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<RoleRecord | 'new' | null>(null);
  const [deleting, setDeleting] = useState<RoleRecord | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRoles(await api<RoleRecord[]>('/admin/roles'));
    } catch (e) {
      toast.err((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const editable = can('roles.edit');

  if (loading) return <Loading />;

  return (
    <div className="col">
      <div className="row wrap">
        <div>
          <h2>{t('roles.title')}</h2>
          <p className="small muted" style={{ margin: 0 }}>{t('roles.intro')}</p>
        </div>
        <div className="spacer" />
        {editable ? (
          <Button variant="primary" onClick={() => setEditing('new')}>
            {t('roles.new')}
          </Button>
        ) : null}
      </div>

      <div className="grid auto">
        {roles.map((r) => (
          <div className="card" key={r.id}>
            <div className="card-head">
              <div style={{ minWidth: 0 }}>
                <h3>{lang === 'ar' ? r.nameAr : r.nameEn}</h3>
                <div className="xsmall muted">{r.key}</div>
              </div>
              <div className="spacer" />
              {r.builtIn ? <Pill>{t('roles.builtIn')}</Pill> : <Pill kind="accent">{t('roles.custom')}</Pill>}
            </div>

            <div className="row wrap" style={{ gap: 'var(--s-2)' }}>
              <Pill kind="info">{t('roles.permissionCount', { n: r.permissions.length })}</Pill>
              <Pill>{t('roles.people', { n: r.userCount ?? 0 })}</Pill>
              <Pill>{t('roles.discountLimit', { n: r.maxDiscountPercent })}</Pill>
            </div>

            <p className="xsmall muted" style={{ marginBlockStart: 'var(--s-3)' }}>
              {summarise(r, lang)}
            </p>

            {editable ? (
              <div className="btn-row" style={{ marginBlockStart: 'var(--s-3)' }}>
                <Button size="sm" onClick={() => setEditing(r)}>
                  {r.key === 'owner' ? t('common.edit') : t('roles.editPermissions')}
                </Button>
                {!r.builtIn ? (
                  <Button size="sm" variant="ghost" onClick={() => setDeleting(r)}>
                    {t('common.delete')}
                  </Button>
                ) : null}
              </div>
            ) : null}
          </div>
        ))}
      </div>

      {editing ? (
        <RoleEditor
          role={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load();
            // The owner may have just changed their own powers.
            void refreshUser();
          }}
        />
      ) : null}

      {deleting ? (
        <Confirm
          title={t('roles.delete')}
          message={
            (deleting.userCount ?? 0) > 0
              ? t('roles.deleteBlocked', { n: deleting.userCount ?? 0 })
              : t('roles.deleteConfirm')
          }
          confirmLabel={t('common.delete')}
          danger
          onConfirm={() => {
            void api(`/admin/roles/${deleting.id}`, { method: 'DELETE' })
              .then(() => {
                toast.ok(t('common.deleted'));
                void load();
              })
              .catch((e: Error) => toast.err(e.message));
            setDeleting(null);
          }}
          onCancel={() => setDeleting(null)}
        />
      ) : null}
    </div>
  );
}

/** A one-line human summary of what a role is for. */
function summarise(r: RoleRecord, lang: string): string {
  const has = (p: Permission) => r.permissions.includes(p);
  const bits: string[] = [];
  const en = lang !== 'ar';
  if (has('till.sell')) bits.push(en ? 'sells' : 'يبيع');
  if (has('exchange.do')) bits.push(en ? 'exchanges' : 'يستبدل');
  if (has('stock.receive')) bits.push(en ? 'receives stock' : 'يستلم بضاعة');
  if (has('reports.profit')) bits.push(en ? 'sees profit' : 'يشوف الأرباح');
  else if (has('reports.sales')) bits.push(en ? 'sees sales' : 'يشوف المبيعات');
  if (has('users.edit')) bits.push(en ? 'manages staff' : 'يدير الموظفين');
  if (has('settings.edit')) bits.push(en ? 'changes settings' : 'يغيّر الإعدادات');
  return bits.join(' · ');
}

// ---------------------------------------------------------------------------
// The editor
// ---------------------------------------------------------------------------

export function RoleEditor({
  role,
  onClose,
  onSaved,
}: {
  role: RoleRecord | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const t = useT();
  const { lang } = useI18n();
  const shopPermissions = useShopPermissions();
  const toast = useToast();
  const isOwnerRole = role?.key === 'owner';

  const [nameEn, setNameEn] = useState(role?.nameEn ?? '');
  const [nameAr, setNameAr] = useState(role?.nameAr ?? '');
  const [maxDiscount, setMaxDiscount] = useState(role?.maxDiscountPercent ?? 10);
  const [chosen, setChosen] = useState<Set<Permission>>(new Set(role?.permissions ?? []));
  const [busy, setBusy] = useState(false);

  const toggle = (p: Permission) =>
    setChosen((cur) => {
      const next = new Set(cur);
      if (next.has(p)) next.delete(p);
      else next.add(p);
      return next;
    });

  const toggleGroup = (g: PermissionGroup, on: boolean) =>
    setChosen((cur) => {
      const next = new Set(cur);
      for (const p of shopPermissions.filter((x) => x.group === g)) {
        if (on) next.add(p.key);
        else next.delete(p.key);
      }
      return next;
    });

  const save = async () => {
    setBusy(true);
    try {
      const body = {
        nameEn: nameEn.trim(),
        nameAr: nameAr.trim(),
        maxDiscountPercent: maxDiscount,
        permissions: [...chosen],
      };
      if (role) await api(`/admin/roles/${role.id}`, { method: 'PUT', body });
      else await api('/admin/roles', { body });
      toast.ok(t('common.saved'));
      onSaved();
    } catch (e) {
      toast.err((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={role ? (lang === 'ar' ? role.nameAr : role.nameEn) : t('roles.new')}
      wide
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <div className="spacer" />
          <span className="small muted">{t('roles.permissionCount', { n: chosen.size })}</span>
          <Button variant="primary" busy={busy} disabled={!nameEn.trim() && !nameAr.trim()} onClick={() => void save()}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="grid c3">
        <Field label={`${t('common.name')} (العربية)`}>
          <input value={nameAr} onChange={(e) => setNameAr(e.target.value)} dir="rtl" />
        </Field>
        <Field label={`${t('common.name')} (English)`}>
          <input value={nameEn} onChange={(e) => setNameEn(e.target.value)} dir="ltr" />
        </Field>
        <Field label={t('roles.discountLimitLabel')}>
          <input
            type="number"
            min={0}
            max={100}
            value={maxDiscount}
            onChange={(e) => setMaxDiscount(Number(e.target.value))}
            className="tnum"
          />
        </Field>
      </div>

      {isOwnerRole ? (
        <div className="note-info" style={{ marginBlockStart: 'var(--s-4)' }}>{t('roles.ownerLocked')}</div>
      ) : null}

      <div style={{ marginBlockStart: 'var(--s-5)' }}>
        {PERMISSION_GROUPS.map((g) => {
          const perms = shopPermissions.filter((p) => p.group === g.key);
          if (perms.length === 0) return null;
          const on = perms.filter((p) => chosen.has(p.key)).length;
          return (
            <div key={g.key} className="perm-group">
              <div className="perm-head">
                <strong>{lang === 'ar' ? g.ar : g.en}</strong>
                <span className="xsmall muted">{on}/{perms.length}</span>
                <div className="spacer" />
                {!isOwnerRole ? (
                  <>
                    <Button size="sm" variant="ghost" onClick={() => toggleGroup(g.key, true)}>
                      {t('common.all')}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => toggleGroup(g.key, false)}>
                      {t('common.none')}
                    </Button>
                  </>
                ) : null}
              </div>
              <div className="perm-list">
                {perms.map((p: PermissionMeta) => (
                  <label key={p.key} className={`perm ${chosen.has(p.key) ? 'on' : ''}`}>
                    <input
                      type="checkbox"
                      checked={chosen.has(p.key)}
                      disabled={isOwnerRole}
                      onChange={() => toggle(p.key)}
                    />
                    <span className="grow">
                      {lang === 'ar' ? p.ar : p.en}
                      {p.sensitive ? <span className="perm-flag">{t('roles.sensitive')}</span> : null}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </Modal>
  );
}

/**
 * Give one person one power their role does not carry, or take one away that it
 * does - without disturbing anybody else who shares the role.
 */
export function UserPermissionEditor({
  userId,
  rolePermissions,
  overrides,
  onChange,
}: {
  userId: string;
  rolePermissions: Permission[];
  overrides: { granted: Permission[]; revoked: Permission[] };
  onChange: (o: { granted: Permission[]; revoked: Permission[] }) => void;
}) {
  const t = useT();
  const { lang } = useI18n();
  const shopPermissions = useShopPermissions();

  const state = useMemo(() => {
    const map = new Map<Permission, 'role' | 'granted' | 'revoked' | 'no'>();
    for (const p of PERMISSIONS) {
      if (overrides.granted.includes(p.key)) map.set(p.key, 'granted');
      else if (overrides.revoked.includes(p.key)) map.set(p.key, 'revoked');
      else map.set(p.key, rolePermissions.includes(p.key) ? 'role' : 'no');
    }
    return map;
  }, [rolePermissions, overrides]);

  const cycle = (p: Permission) => {
    const inRole = rolePermissions.includes(p);
    const now = state.get(p);
    const granted = new Set(overrides.granted);
    const revoked = new Set(overrides.revoked);
    granted.delete(p);
    revoked.delete(p);

    if (inRole) {
      // From the role -> taken away -> back to the role.
      if (now !== 'revoked') revoked.add(p);
    } else {
      // Not in the role -> given -> back to not having it.
      if (now !== 'granted') granted.add(p);
    }
    onChange({ granted: [...granted], revoked: [...revoked] });
  };

  return (
    <div>
      <p className="small muted">{t('roles.overrideHint')}</p>
      {PERMISSION_GROUPS.filter((g) => shopPermissions.some((p) => p.group === g.key)).map((g) => (
        <div key={g.key} className="perm-group">
          <div className="perm-head">
            <strong>{lang === 'ar' ? g.ar : g.en}</strong>
          </div>
          <div className="perm-list">
            {shopPermissions.filter((p) => p.group === g.key).map((p) => {
              const s = state.get(p.key);
              return (
                <button
                  key={p.key}
                  type="button"
                  className={`perm perm-btn ${s}`}
                  onClick={() => cycle(p.key)}
                  title={p.key}
                >
                  <span className="grow">{lang === 'ar' ? p.ar : p.en}</span>
                  <span className="perm-state">
                    {s === 'role' ? t('roles.fromRole')
                      : s === 'granted' ? t('roles.added')
                      : s === 'revoked' ? t('roles.removed')
                      : '—'}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
